/**
 * Shared provider registry generation logic
 * Used by both the CLI generation script and runtime refresh
 */

import fs from 'node:fs/promises';
import path from 'node:path';
import { getCapabilityFileName } from './capability-file.js';
import type {
  AttachmentCapabilities,
  MastraModelGatewayInterface,
  ProviderConfig,
  StructuredOutputCapabilities,
  TemperatureCapabilities,
} from './gateways/base.js';
import { getGatewayId, shouldEnableGateway } from './gateways/index.js';

const WINDOWS_RENAME_RETRY_DELAYS_MS = [25, 50, 100, 200, 400];

interface GatewayWithAttachmentCapabilities {
  getAttachmentCapabilities(): AttachmentCapabilities;
}

interface GatewayWithTemperatureCapabilities {
  getTemperatureCapabilities(): TemperatureCapabilities;
}

interface GatewayWithStructuredOutputCapabilities {
  getStructuredOutputCapabilities(): StructuredOutputCapabilities;
}

function hasAttachmentCapabilities(
  gateway: MastraModelGatewayInterface,
): gateway is MastraModelGatewayInterface & GatewayWithAttachmentCapabilities {
  return 'getAttachmentCapabilities' in gateway && typeof gateway.getAttachmentCapabilities === 'function';
}

function hasTemperatureCapabilities(
  gateway: MastraModelGatewayInterface,
): gateway is MastraModelGatewayInterface & GatewayWithTemperatureCapabilities {
  return 'getTemperatureCapabilities' in gateway && typeof gateway.getTemperatureCapabilities === 'function';
}

function hasStructuredOutputCapabilities(
  gateway: MastraModelGatewayInterface,
): gateway is MastraModelGatewayInterface & GatewayWithStructuredOutputCapabilities {
  return 'getStructuredOutputCapabilities' in gateway && typeof gateway.getStructuredOutputCapabilities === 'function';
}

/**
 * Write a file atomically using the write-to-temp-then-rename pattern.
 * This prevents file corruption when multiple processes write to the same file concurrently.
 *
 * The rename operation is atomic on POSIX systems when source and destination
 * are on the same filesystem.
 *
 * @param filePath - The target file path
 * @param content - The content to write
 * @param encoding - The encoding to use (default: 'utf-8')
 */
export async function atomicWriteFile(
  filePath: string,
  content: string,
  encoding: BufferEncoding = 'utf-8',
): Promise<void> {
  // Create a unique temp file name using PID, timestamp, and random suffix to avoid collisions
  const randomSuffix = Math.random().toString(36).substring(2, 15);
  const tempPath = `${filePath}.${process.pid}.${Date.now()}.${randomSuffix}.tmp`;

  try {
    // Write to temp file first
    await fs.writeFile(tempPath, content, encoding);

    // Atomically rename temp file to target path. Windows scanners can briefly
    // lock the destination while the write is otherwise complete.
    for (let attempt = 0; ; attempt += 1) {
      try {
        await fs.rename(tempPath, filePath);
        break;
      } catch (error) {
        const delayMs = WINDOWS_RENAME_RETRY_DELAYS_MS[attempt];
        const retryable =
          process.platform === 'win32' &&
          error instanceof Error &&
          'code' in error &&
          ((error as NodeJS.ErrnoException).code === 'EPERM' || (error as NodeJS.ErrnoException).code === 'EBUSY');

        if (!retryable || delayMs === undefined) throw error;
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  } catch (error) {
    // Clean up temp file if it exists
    try {
      await fs.unlink(tempPath);
    } catch {
      // Ignore cleanup errors
    }
    throw error;
  }
}

/**
 * Fetch providers from all enabled gateways with silent retry logic.
 * Retries up to 3 times per gateway with exponential backoff. If all
 * retries are exhausted the gateway is silently skipped (no error logging)
 * since the bundled registry already contains all model data.
 * @param gateways - Array of gateway instances to fetch from
 * @returns Object containing providers and models records
 */
export async function fetchProvidersFromGateways(gateways: MastraModelGatewayInterface[]): Promise<{
  providers: Record<string, ProviderConfig>;
  models: Record<string, string[]>;
  attachmentCapabilities: AttachmentCapabilities;
  temperatureCapabilities: TemperatureCapabilities;
  structuredOutputCapabilities: StructuredOutputCapabilities;
  failedGateways: string[];
}> {
  const enabledGateways: MastraModelGatewayInterface[] = [];

  for (const gateway of gateways) {
    if (shouldEnableGateway(gateway)) {
      enabledGateways.push(gateway);
    }
  }

  const allProviders: Record<string, ProviderConfig> = {};
  const allModels: Record<string, string[]> = {};
  const allAttachmentCapabilities: AttachmentCapabilities = {};
  const allTemperatureCapabilities: TemperatureCapabilities = {};
  const allStructuredOutputCapabilities: StructuredOutputCapabilities = {};
  const failedGateways: string[] = [];

  const maxRetries = 3;

  for (const gateway of enabledGateways) {
    let providers: Record<string, ProviderConfig> | null = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        providers = await gateway.fetchProviders();
        break;
      } catch {
        if (attempt < maxRetries) {
          const delayMs = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }

    if (!providers) {
      failedGateways.push(getGatewayId(gateway));
      continue;
    }

    const gatewayId = getGatewayId(gateway);
    // models.dev is a provider registry, not a true gateway - don't prefix its providers
    const isProviderRegistry = gatewayId === 'models.dev';

    // Collect capabilities if the gateway exposes them
    const gatewayAttachmentCaps = hasAttachmentCapabilities(gateway) ? gateway.getAttachmentCapabilities() : undefined;
    const gatewayTemperatureCaps = hasTemperatureCapabilities(gateway)
      ? gateway.getTemperatureCapabilities()
      : undefined;
    const gatewayStructuredOutputCaps = hasStructuredOutputCapabilities(gateway)
      ? gateway.getStructuredOutputCapabilities()
      : undefined;

    for (const [providerId, config] of Object.entries(providers)) {
      // For true gateways, use gateway id as prefix (e.g., "netlify/anthropic")
      // Special case: if providerId matches gateway id, it's a unified gateway (e.g., azure-openai returning {azure-openai: {...}})
      // In this case, use just the gateway ID to avoid duplication (azure-openai, not azure-openai/azure-openai)
      const typeProviderId = isProviderRegistry
        ? providerId
        : providerId === gatewayId
          ? gatewayId
          : `${gatewayId}/${providerId}`;

      allProviders[typeProviderId] = config;
      // Sort models alphabetically for consistent ordering
      allModels[typeProviderId] = config.models.sort();

      // Merge capabilities for this provider if available
      if (gatewayAttachmentCaps?.[providerId]) {
        allAttachmentCapabilities[typeProviderId] = gatewayAttachmentCaps[providerId];
      }
      if (gatewayTemperatureCaps?.[providerId]) {
        allTemperatureCapabilities[typeProviderId] = gatewayTemperatureCaps[providerId];
      }
      if (gatewayStructuredOutputCaps?.[providerId]) {
        allStructuredOutputCapabilities[typeProviderId] = gatewayStructuredOutputCaps[providerId];
      }
    }
  }

  return {
    providers: allProviders,
    models: allModels,
    attachmentCapabilities: allAttachmentCapabilities,
    temperatureCapabilities: allTemperatureCapabilities,
    structuredOutputCapabilities: allStructuredOutputCapabilities,
    failedGateways,
  };
}

/**
 * Generate TypeScript type definitions content
 * @param models - Record of provider IDs to model arrays
 * @returns Generated TypeScript type definitions as a string
 */
export function generateTypesContent(models: Record<string, string[]>): string {
  const providerModelsEntries = Object.entries(models)
    .map(([provider, modelList]) => {
      const modelsList = modelList.map(m => `'${m}'`);

      // Quote provider key if it's not a valid JavaScript identifier
      // Valid identifiers must start with a letter, underscore, or dollar sign
      const needsQuotes = !/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(provider);
      const providerKey = needsQuotes ? `'${provider}'` : provider;

      // Format array based on the repository printWidth of 120.
      const singleLine = `  readonly ${providerKey}: readonly [${modelsList.join(', ')}];`;

      // If single line exceeds 120 chars, format as multi-line
      if (singleLine.length > 120) {
        const formattedModels = modelList.map(m => `    '${m}',`).join('\n');
        return `  readonly ${providerKey}: readonly [\n${formattedModels}\n  ];`;
      }

      return singleLine;
    })
    .join('\n');

  // The Provider / ModelForProvider / ModelRouterModelId types emitted below cover
  // the built-in providers only. The public, augmentable versions live in
  // `src/llm/index.ts`, which re-derives them from the `ProviderModelsMap`
  // interface so custom gateways can extend them. Keep the two in sync, and
  // import from `@mastra/core/llm` rather than from the generated file.
  return `/**
 * THIS FILE IS AUTO-GENERATED - DO NOT EDIT
 * Generated from model gateway providers
 */

/**
 * Provider models mapping type
 * This is derived from the JSON data and provides type-safe access
 */
export type ProviderModelsMap = {
${providerModelsEntries}
};

/**
 * Union type of all registered provider IDs
 */
export type Provider = keyof ProviderModelsMap;

/**
 * Provider models mapping interface
 */
export interface ProviderModels {
  [key: string]: string[];
}

/**
 * OpenAI-compatible model ID type
 * Dynamically derived from ProviderModelsMap
 * Full provider/model paths (e.g., "openai/gpt-4o", "anthropic/claude-3-5-sonnet-20241022")
 */
export type ModelRouterModelId =
  | {
      [P in Provider]: \`\${P}/\${ProviderModelsMap[P][number]}\`;
    }[Provider]
  | \`mastra/\${ProviderModelsMap['openrouter'][number]}\`
  | (string & {});

/**
 * Extract the model part from a ModelRouterModelId for a specific provider
 * Dynamically derived from ProviderModelsMap
 * Example: ModelForProvider<'openai'> = 'gpt-4o' | 'gpt-4-turbo' | ...
 */
export type ModelForProvider<P extends Provider> = ProviderModelsMap[P][number];
`;
}

/**
 * Write registry files to disk (JSON and .d.ts)
 * @param jsonPath - Path to write the JSON file
 * @param typesPath - Path to write the .d.ts file
 * @param providers - Provider configurations
 * @param models - Model lists by provider
 */
export async function writeRegistryFiles(
  jsonPath: string,
  typesPath: string,
  providers: Record<string, ProviderConfig>,
  models: Record<string, string[]>,
  attachmentCapabilities?: AttachmentCapabilities,
  temperatureCapabilities?: TemperatureCapabilities,
  structuredOutputCapabilities?: StructuredOutputCapabilities,
): Promise<void> {
  // 0. Ensure directories exist
  const jsonDir = path.dirname(jsonPath);
  const typesDir = path.dirname(typesPath);
  await fs.mkdir(jsonDir, { recursive: true });
  await fs.mkdir(typesDir, { recursive: true });

  // 1. Write JSON file atomically to prevent corruption from concurrent writes
  const registryData = {
    providers,
    models,
    version: '1.0.0',
  };

  await atomicWriteFile(jsonPath, JSON.stringify(registryData, null, 2), 'utf-8');

  // 2. Generate .d.ts file with type-only declarations (also atomic)
  const typeContent = generateTypesContent(models);
  await atomicWriteFile(typesPath, typeContent, 'utf-8');

  // 3. Write per-provider capability files into a capabilities/ directory
  const capDir = path.join(jsonDir, 'capabilities');
  const hasCapabilities =
    (attachmentCapabilities && Object.keys(attachmentCapabilities).length > 0) ||
    (temperatureCapabilities && Object.keys(temperatureCapabilities).length > 0) ||
    (structuredOutputCapabilities && Object.keys(structuredOutputCapabilities).length > 0);

  // Remove stale files even when the latest registry has no capability data.
  await fs.rm(capDir, { recursive: true, force: true });

  if (hasCapabilities) {
    await fs.mkdir(capDir, { recursive: true });

    // Build a merged capability object per provider
    const allProviderIds = new Set([
      ...(attachmentCapabilities ? Object.keys(attachmentCapabilities) : []),
      ...(temperatureCapabilities ? Object.keys(temperatureCapabilities) : []),
      ...(structuredOutputCapabilities ? Object.keys(structuredOutputCapabilities) : []),
    ]);

    for (const provider of allProviderIds) {
      const capData: Record<string, string[]> = {};
      if (attachmentCapabilities?.[provider]) capData.attachment = attachmentCapabilities[provider];
      if (temperatureCapabilities?.[provider]) capData.temperature = temperatureCapabilities[provider];
      if (structuredOutputCapabilities?.[provider]) {
        capData.structuredOutput = structuredOutputCapabilities[provider];
      }

      const providerFile = path.join(capDir, getCapabilityFileName(provider));
      await atomicWriteFile(providerFile, JSON.stringify(capData, null, 2), 'utf-8');
    }
  }
}
